import { useState, useEffect, useRef } from "react";
import { CAPABILITY_CATEGORIES } from "../../../mocks/capabilities";

const JOB_TILES = [
  { id: "landscaping", label: "Landscaping", icon: "ri-plant-line", pay: "$18–25/hr", color: "bg-green-50 border-green-200", active: "bg-green-500 border-green-500", iconColor: "text-green-600", image: "https://public.readdy.ai/ai/img_res/c79f8ed28a55bd9f5509b0c8a98be185.jpg" },
  { id: "moving", label: "Moving & Labor", icon: "ri-truck-line", pay: "$20–30/hr", color: "bg-orange-50 border-orange-200", active: "bg-orange-500 border-orange-500", iconColor: "text-orange-600", image: "https://readdy.ai/api/search-image?query=movers%20carrying%20boxes%20furniture%20into%20truck%20daytime%20residential%20bright%20clean&width=300&height=200&seq=ct2&orientation=landscape" },
  { id: "cleaning", label: "Cleaning", icon: "ri-brush-2-line", pay: "$16–22/hr", color: "bg-sky-50 border-sky-200", active: "bg-sky-500 border-sky-500", iconColor: "text-sky-600", image: "https://readdy.ai/api/search-image?query=cleaner%20cleaning%20bright%20modern%20home%20white%20kitchen%20sparkling%20surfaces%20cheerful%20uniform&width=300&height=200&seq=ct3&orientation=landscape" },
  { id: "construction", label: "Construction", icon: "ri-hammer-line", pay: "$22–38/hr", color: "bg-amber-50 border-amber-200", active: "bg-amber-500 border-amber-500", iconColor: "text-amber-600", image: "https://readdy.ai/api/search-image?query=construction%20worker%20hard%20hat%20framing%20building%20site%20daylight%20confident%20tools&width=300&height=200&seq=ct4&orientation=landscape" },
  { id: "events", label: "Events & Staffing", icon: "ri-calendar-event-line", pay: "$15–20/hr", color: "bg-rose-50 border-rose-200", active: "bg-rose-500 border-rose-500", iconColor: "text-rose-600", image: "https://readdy.ai/api/search-image?query=event%20staff%20setting%20up%20elegant%20venue%20chairs%20tables%20banquet%20organized%20professional%20crew&width=300&height=200&seq=ct5&orientation=landscape" },
  { id: "driving", label: "Driving & Delivery", icon: "ri-car-line", pay: "$18–28/hr", color: "bg-slate-50 border-slate-200", active: "bg-slate-600 border-slate-600", iconColor: "text-slate-600", image: "https://readdy.ai/api/search-image?query=delivery%20driver%20smiling%20holding%20packages%20door%20van%20residential%20daytime%20professional%20neat&width=300&height=200&seq=ct6&orientation=landscape" },
];

const PAY_RANGES: Record<string, [number, number]> = {
  landscaping: [18, 25], moving: [20, 30], cleaning: [16, 22],
  construction: [22, 38], events: [15, 20], driving: [18, 28],
};
const SKILL_BONUS = 1.4;
const WEEKLY_HOURS = 20;

function useCountUp(target: number, duration = 600) {
  const [display, setDisplay] = useState(target);
  const frame = useRef<number>(0);
  const start = useRef<number>(0);
  const from = useRef<number>(target);

  useEffect(() => {
    from.current = display;
    start.current = performance.now();
    cancelAnimationFrame(frame.current);
    const animate = (now: number) => {
      const elapsed = now - start.current;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from.current + (target - from.current) * ease));
      if (progress < 1) frame.current = requestAnimationFrame(animate);
    };
    frame.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return display;
}

export default function CapabilitiesSection() {
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [expanded, setExpanded] = useState<string | null>("physical");
  const [matchCount, setMatchCount] = useState<number | null>(null);

  const toggleJob = (id: string) => {
    setSelectedJobs((p) => p.includes(id) ? p.filter((j) => j !== id) : [...p, id]);
    setMatchCount(null);
  };

  const toggleSkill = (catId: string, skill: string) => {
    setSelected((p) => {
      const cur = p[catId] || [];
      return { ...p, [catId]: cur.includes(skill) ? cur.filter((s) => s !== skill) : [...cur, skill] };
    });
    setMatchCount(null);
  };

  const totalSkills = Object.values(selected).flat().length;
  const totalSelected = totalSkills + selectedJobs.length;

  const calcEarnings = () => {
    if (selectedJobs.length === 0) return [0, 0];
    const rates = selectedJobs.map((j) => PAY_RANGES[j] || [15, 20]);
    const avgLow = rates.reduce((s, r) => s + r[0], 0) / rates.length;
    const avgHigh = rates.reduce((s, r) => s + r[1], 0) / rates.length;
    const bonus = 1 + (totalSkills * (SKILL_BONUS - 1)) / 10;
    return [Math.round(avgLow * WEEKLY_HOURS * Math.min(bonus, 1.5)), Math.round(avgHigh * WEEKLY_HOURS * Math.min(bonus, 1.5))];
  };

  const [earnLow, earnHigh] = calcEarnings();
  const displayLow = useCountUp(earnLow);
  const displayHigh = useCountUp(earnHigh);

  const profileStrength = Math.min(Math.round((totalSelected / 12) * 100), 100);

  const strengthLabel = profileStrength === 0 ? "Not started" : profileStrength < 30 ? "Getting started" : profileStrength < 60 ? "Looking good!" : profileStrength < 85 ? "Strong profile" : "Top earner tier!";
  const strengthColor = profileStrength === 0 ? "bg-gray-200" : profileStrength < 30 ? "bg-rose-400" : profileStrength < 60 ? "bg-amber-400" : profileStrength < 85 ? "bg-emerald-400" : "bg-emerald-500";

  const handleFindMatches = () => {
    const base = 120 + totalSelected * 47;
    setMatchCount(Math.min(base, 847));
    const el = document.getElementById("marketplace");
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 600);
  };

  return (
    <section id="capabilities" className="py-16 md:py-24 bg-[#f7f9f8] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-4 py-1.5 rounded-full mb-4">
            Instant Skill Match
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
            What can <span className="text-emerald-500">you earn</span> right now?
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto">
            Tap your skills below and watch your earning potential calculate in real time. No resume needed.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Left: interactive selector */}
          <div className="lg:col-span-3 flex flex-col gap-5">

            {/* Step 1: Job types */}
            <div className="bg-white rounded-2xl p-5 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 flex items-center justify-center bg-emerald-500 rounded-full flex-shrink-0">
                  <span className="text-white text-xs font-extrabold">1</span>
                </div>
                <h3 className="font-extrabold text-gray-900 text-base">What work do you want?</h3>
                <span className="ml-auto text-xs text-gray-400">{selectedJobs.length} selected</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {JOB_TILES.map((job) => {
                  const isActive = selectedJobs.includes(job.id);
                  return (
                    <button
                      key={job.id}
                      onClick={() => toggleJob(job.id)}
                      className={`relative rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer group ${isActive ? "border-emerald-500" : "border-transparent"}`}
                    >
                      <div className="w-full h-20 overflow-hidden">
                        <img src={job.image} alt={job.label} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                      </div>
                      {isActive && (
                        <div className="absolute inset-0 bg-emerald-500/20" />
                      )}
                      {isActive && (
                        <div className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center bg-emerald-500 rounded-full">
                          <i className="ri-check-line text-white text-[10px]" />
                        </div>
                      )}
                      <div className="p-2 bg-white">
                        <div className="text-xs font-bold text-gray-900 truncate">{job.label}</div>
                        <div className="text-[10px] text-emerald-600 font-semibold">{job.pay}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Skills accordion */}
            <div className="bg-white rounded-2xl p-5 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 flex items-center justify-center bg-emerald-500 rounded-full flex-shrink-0">
                  <span className="text-white text-xs font-extrabold">2</span>
                </div>
                <h3 className="font-extrabold text-gray-900 text-base">Pick your skills</h3>
                <span className="ml-auto text-xs text-gray-400">{totalSkills} selected</span>
              </div>
              <div className="flex flex-col gap-2">
                {CAPABILITY_CATEGORIES.map((cat) => {
                  const catSelected = selected[cat.id] || [];
                  const isOpen = expanded === cat.id;
                  return (
                    <div key={cat.id} className="rounded-xl border border-gray-100 overflow-hidden">
                      <button
                        onClick={() => setExpanded(isOpen ? null : cat.id)}
                        className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer text-left"
                      >
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 flex-shrink-0`}>
                          <i className={`${cat.icon} ${cat.iconColor} text-base`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-gray-900">{cat.title}</div>
                        </div>
                        {catSelected.length > 0 && (
                          <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {catSelected.length} picked
                          </span>
                        )}
                        <i className={`ri-arrow-down-s-line text-gray-400 text-lg transition-transform duration-200 flex-shrink-0 ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                      {isOpen && (
                        <div className="px-4 py-3 flex flex-wrap gap-2 bg-white">
                          {cat.skills.map((skill) => {
                            const active = catSelected.includes(skill);
                            return (
                              <button
                                key={skill}
                                onClick={() => toggleSkill(cat.id, skill)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all duration-150 cursor-pointer whitespace-nowrap ${
                                  active
                                    ? "bg-emerald-500 border-emerald-500 text-white"
                                    : "bg-gray-50 border-gray-200 text-gray-600 hover:border-emerald-300 hover:text-emerald-700"
                                }`}
                              >
                                {active && <i className="ri-check-line mr-1" />}
                                {skill}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: live earnings panel */}
          <div className="lg:col-span-2 flex flex-col gap-5">

            {/* Earnings estimate */}
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl p-6 relative overflow-hidden">
              <div className="pointer-events-none absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10" />
              <div className="pointer-events-none absolute bottom-0 left-0 w-24 h-24 rounded-full bg-white/5" />

              <div className="text-emerald-100 text-xs font-extrabold uppercase tracking-widest mb-2">Your Earning Potential</div>

              {earnLow === 0 ? (
                <div className="text-white/70 text-sm italic mb-2">Select work types above to see your estimate</div>
              ) : (
                <>
                  <div className="flex items-end gap-1 mb-1">
                    <span className="text-white text-4xl font-black">${displayLow}–${displayHigh}</span>
                  </div>
                  <div className="text-emerald-100 text-xs mb-4">estimated per week · part time ({WEEKLY_HOURS}hrs)</div>
                </>
              )}

              {/* Skill bonus indicator */}
              <div className="bg-white/10 rounded-xl px-4 py-3 mb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-bold text-white">Skills bonus</span>
                  <span className="text-xs font-extrabold text-emerald-200">+{Math.round((Math.min(1 + totalSkills * (SKILL_BONUS - 1) / 10, 1.5) - 1) * 100)}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-white/20 overflow-hidden">
                  <div
                    className="h-2 rounded-full bg-white transition-all duration-500"
                    style={{ width: `${Math.min((totalSkills / 12) * 100, 100)}%` }}
                  />
                </div>
                <div className="text-[10px] text-emerald-200 mt-1">Add more skills to unlock higher rates</div>
              </div>

              <button
                onClick={handleFindMatches}
                disabled={totalSelected === 0}
                className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  totalSelected > 0
                    ? "bg-white text-emerald-700 hover:bg-emerald-50"
                    : "bg-white/20 text-white/50 cursor-not-allowed"
                }`}
              >
                <i className="ri-flashlight-line mr-2" />
                {matchCount !== null ? `${matchCount} Matches Found! →` : "Find My Matches"}
              </button>
            </div>

            {/* Profile strength */}
            <div className="bg-white rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-extrabold text-gray-900 text-sm">Profile Strength</h4>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${profileStrength >= 85 ? "bg-emerald-100 text-emerald-700" : profileStrength >= 60 ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-500"}`}>
                  {strengthLabel}
                </span>
              </div>
              <div className="w-full h-3 rounded-full bg-gray-100 overflow-hidden mb-2">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${strengthColor}`}
                  style={{ width: `${profileStrength}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-400 mb-4">
                <span>{profileStrength}% complete</span>
                <span>{totalSelected} / 12 selections</span>
              </div>

              {/* Perks unlocked */}
              <div className="flex flex-col gap-2">
                {[
                  { label: "Basic job matches", threshold: 0, icon: "ri-check-line" },
                  { label: "AI resume generation", threshold: 3, icon: "ri-file-text-line" },
                  { label: "Priority placement", threshold: 6, icon: "ri-arrow-up-line" },
                  { label: "Top earner badge", threshold: 10, icon: "ri-award-line" },
                ].map((perk) => {
                  const unlocked = totalSelected >= perk.threshold;
                  return (
                    <div key={perk.label} className={`flex items-center gap-2.5 text-xs ${unlocked ? "text-gray-800" : "text-gray-400"}`}>
                      <div className={`w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 ${unlocked ? "bg-emerald-100" : "bg-gray-100"}`}>
                        <i className={`${perk.icon} text-[10px] ${unlocked ? "text-emerald-600" : "text-gray-400"}`} />
                      </div>
                      <span className={unlocked ? "font-semibold" : ""}>{perk.label}</span>
                      {!unlocked && <span className="ml-auto text-[10px] text-gray-400">{perk.threshold - totalSelected} more</span>}
                      {unlocked && <i className="ri-check-fill text-emerald-500 ml-auto text-sm" />}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Social proof nudge */}
            <div className="bg-[#f7f9f8] rounded-2xl p-4 flex items-center gap-3">
              <div className="flex -space-x-2 flex-shrink-0">
                {["av1", "av2", "av3"].map((seq) => (
                  <div key={seq} className="w-8 h-8 rounded-full border-2 border-white overflow-hidden">
                    <img
                      src={`https://readdy.ai/api/search-image?query=worker%20smiling%20headshot%20portrait%20plain%20background&width=32&height=32&seq=${seq}&orientation=squarish`}
                      alt="Worker"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs font-bold text-gray-900">47 workers matched today</div>
                <div className="text-[10px] text-gray-500">Average first match: 4 minutes</div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
